import React, { useContext, useState, useEffect } from "react";
import "../Invoices.css";
import {
  Button,
  Tooltip,
  IconButton,
  Modal,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import ErrorPopup from "../../../components/element/ErrorPopup";
import Loader from "../../../components/element/Loader";
import NoDataPlaceholder from "../../../components/element/NoDataPlaceholder";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import { StorageContext } from "../../../services/StorageProvider";
import {
  ABO_INVOICE_STATUS,
  DATE_FORMAT,
  INVOICE_STATUS_LABEL,
  LAND_LEASE_TYPE,
  PAYMENT_METHOD,
} from "../../../utils/constants";
import {
  handleEmptyValue,
  formatToTwoDecimal,
  getPaymentMethodDisplay,
  getPaymentStatus,
  formatNumber,
} from "../../../utils/utils";
import dayjs from "dayjs";
import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import PaymentTable from "./PaymentTable";

const InvoicesTable = ({
  invoiceData,
  isLoading,
  error,
  onViewClick,
  onSelectionChange,
  selectedTab,
  selectedInvoices: parentSelectedInvoices = [],
  currentPage = 0,
  pageSize = 10,
  onDownloadInvoice,
  downloadingInvoiceId,
  showLandUseCategory = false,
}) => {
  const { fboListData } = useContext(StorageContext);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "error",
  });

  useEffect(() => {
    setSelectedInvoices(parentSelectedInvoices);
  }, [parentSelectedInvoices]);

  // Reset snackbar state whenever the selected tab or invoice data changes
  useEffect(() => {
    setSnackbar({
      open: false,
      message: "",
      type: "error",
    });
  }, [selectedTab, invoiceData]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedInvoices(invoiceData?.content || []);
    } else {
      setSelectedInvoices([]);
    }
    onSelectionChange(e.target.checked ? invoiceData?.content || [] : []);
  };

  const handleSelectInvoice = (e, invoice) => {
    const updatedSelection = e.target.checked
      ? [...selectedInvoices, invoice]
      : selectedInvoices.filter((item) => item.id !== invoice.id);

    setSelectedInvoices(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const handlePaymentReceiptClick = (item) => {
    // Close any existing snackbar first
    setSnackbar({ open: false, message: "", type: "error" });

    if (!item.payments || item.payments.length === 0) {
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: "No payment receipt found!",
          type: "error",
        });
      }, 100);
      return;
    }
    setSelectedRow(item);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      open: false,
      message: "",
      severity: "info",
    });
  };

  if (error) {
    if (error.status === 404) {
      return <NoDataPlaceholder />;
    } else {
      return <ErrorPopup children={error.message}></ErrorPopup>;
    }
  }

  if (isLoading) {
    return <Loader />;
  }

  if (invoiceData && invoiceData?.content?.length === 0) {
    return <NoDataPlaceholder />;
  }

  return (
    <div className="invoices-table-parent-container">
      <div className="invoices-table-container with-footer">
        <table className="invoices-table">
          <thead>
            <tr>
              <th className="width-90">
                {selectedTab === 0 || selectedTab === 2 ? (
                  <div>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        selectedInvoices.length === invoiceData?.content?.length
                      }
                    />
                    No.
                  </div>
                ) : (
                  <div>No.</div>
                )}
              </th>
              <th>Invoice no.</th>
              <th>Invoice date</th>
              <th>Currency</th>
              <th>Invoice amount</th>
              <th>ABO Name</th>
              <th>Customer No.</th>
              {showLandUseCategory && <th>Land Use Category</th>}
              <th>View invoice</th>
              {(selectedTab === 1 || selectedTab === 2) && (
                <th>Payment Receipt</th>
              )}
              {selectedTab === 1 && (
                <>
                  <th>Payment Method</th>
                  <th>Payment Date</th>
                  <th>Approved Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </>
              )}
              {selectedTab === 2 && (
                <>
                  <th>Paid Amount</th>
                  <th>Payment Ref. No</th>
                  <th>Payment Method</th>
                  <th>Payment Date</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {invoiceData?.content?.map((item, index) => (
              <React.Fragment key={index}>
                <tr key={index}>
                  <td>
                    {selectedTab === 0 || selectedTab === 2 ? (
                      <div>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.some(
                            (invoice) => invoice.id === item.id
                          )}
                          onChange={(e) => handleSelectInvoice(e, item)}
                        />
                        {currentPage * pageSize + index + 1}
                      </div>
                    ) : (
                      <div>{currentPage * pageSize + index + 1}</div>
                    )}
                  </td>
                  <td>{item?.dynamicsInvoiceNumber}</td>
                  <td>{dayjs(item?.invoiceDate).format(DATE_FORMAT)}</td>
                  <td>BSD</td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {selectedTab === 1 || selectedTab === 2
                        ? `BSD ${handleEmptyValue(
                          item?.totalAmount?.toFixed(2)
                        )}`
                        : `BSD ${handleEmptyValue(
                          (
                            (item?.totalAmount || 0) -
                            (item?.totalPaymentReceived || 0)
                          ).toFixed(2)
                        )}`}
                      {item?.totalPaymentReceived > 0 &&
                        selectedTab !== 1 && (
                          <Tooltip
                            title={
                              <div
                                style={{
                                  fontSize: "11px",
                                  fontFamily: "Inter, sans-serif",
                                }}
                              >
                                <div>
                                  Invoice Amount: BSD{" "}
                                  {formatToTwoDecimal(item.totalAmount)}
                                </div>
                                <div>
                                  Paid Amount: BSD{" "}
                                  {formatToTwoDecimal(
                                    item.totalPaymentReceived
                                  )}
                                </div>
                              </div>
                            }
                            arrow
                            placement="bottom"
                            classes={{ tooltip: "partial-payment-tooltip" }}
                          >
                            <IconButton
                              size="small"
                              className="partial-payment-info-icon"
                            >
                              <InfoCircleOutlined />
                            </IconButton>
                          </Tooltip>
                        )}
                    </div>
                  </td>
                  <td>
                    {
                      fboListData?.data?.find(
                        (fbo) => fbo.id === item?.fboCustomerId
                      )?.name
                    }
                  </td>
                  <td>{item?.customerNumber}</td>
                  {showLandUseCategory && (
                    <td>
                      {handleEmptyValue(
                        LAND_LEASE_TYPE.find(
                          (landLeaseType) =>
                            landLeaseType.value === item?.invoiceType
                        )?.name
                      )}
                    </td>
                  )}
                  <td>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => onViewClick(item?.id)}
                    >
                      View
                    </Button>
                  </td>
                  {(selectedTab === 1 || selectedTab === 2) && (
                    <td>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handlePaymentReceiptClick(item)}
                      >
                        View
                      </Button>
                    </td>
                  )}
                  {selectedTab === 1 && (
                    <>
                      <td>
                        {item?.paymentMethod
                          ? PAYMENT_METHOD[item.paymentMethod]
                          : "---"}
                      </td>
                      <td>
                        {item?.paymentDate
                          ? dayjs(item?.paymentDate).format(DATE_FORMAT)
                          : "---"}
                      </td>
                      <td>
                        {item?.settlementDate
                          ? dayjs(item?.settlementDate).format(DATE_FORMAT)
                          : "---"}
                      </td>
                      <td>{INVOICE_STATUS_LABEL[item?.status]}</td>
                    </>
                  )}
                  {selectedTab === 2 && (
                    <>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {`BSD ${item?.totalPaymentReceived.toFixed(2)}`}
                          {(item?.totalPaymentReceived || 0) >
                            (item?.totalAmount || 0) && (
                              <Tooltip
                                title={
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      fontFamily: "Inter, sans-serif",
                                    }}
                                  >
                                    There is a credit balance of BSD{" "}
                                    {formatToTwoDecimal(
                                      (item?.totalPaymentReceived || 0) -
                                      (item?.totalAmount || 0)
                                    )}
                                    .
                                  </div>
                                }
                                arrow
                                placement="bottom"
                                classes={{ tooltip: "partial-payment-tooltip" }}
                              >
                                <IconButton
                                  size="small"
                                  className="partial-payment-info-icon"
                                >
                                  <InfoCircleOutlined />
                                </IconButton>
                              </Tooltip>
                            )}
                        </div>
                      </td>
                      <td>
                        {item?.payments?.length > 0
                          ? item.payments[item.payments.length - 1]
                            ?.transactionNumber || "---"
                          : "---"}
                      </td>
                      <td>
                        {item?.paymentMethod
                          ? PAYMENT_METHOD[item.paymentMethod]
                          : "---"}
                      </td>
                      <td>{dayjs(item?.paymentDate).format(DATE_FORMAT)}</td>
                    </>
                  )}
                  {(selectedTab === 1) && (
                    <td>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => onDownloadInvoice && onDownloadInvoice(item)}
                        disabled={
                          // Disable if already downloading or invoice is not settled
                          downloadingInvoiceId === item.dynamicsInvoiceId ||
                          item?.status !==
                          ABO_INVOICE_STATUS.SETTLED
                        }
                        sx={{
                          minWidth: "100px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {downloadingInvoiceId === item.dynamicsInvoiceId ? (
                          <CircularProgress
                            size={20}
                            color="inherit"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          />
                        ) : (
                          "Download"
                        )}
                      </Button>
                    </td>
                  )}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box className="payment-modal-box">
          <CloseOutlined
            className="payment-modal-close"
            onClick={handleClose}
          />
          <Typography className="payment-modal-title" variant="h6" gutterBottom>
            Payment Receipt
          </Typography>
          {selectedRow && (
            <PaymentTable
              rows={
                selectedRow.payments?.map((payment, index) => ({
                  invoiceNo:
                    payment.dynamicsInvoiceNumber ||
                    selectedRow.dynamicsInvoiceNumber,
                  paymentMethod: getPaymentMethodDisplay(payment.paymentMethod),
                  prn: payment.transactionNumber,
                  grandTotal: `BSD ${formatNumber(payment.paidAmount)}`,
                  Status: getPaymentStatus(payment.remainingAmount),
                })) || []
              }
              paymentMethod={selectedRow.paymentMethod}
              invoiceData={selectedRow}
            />
          )}
        </Box>
      </Modal>

      {snackbar.open && (
        <SnackbarAlert
          type={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </SnackbarAlert>
      )}
    </div>
  );
};

export default InvoicesTable;
